// disallow the following characters
// "(),:;<>[]&!%$+=?@
export const noSpecialCharactersRegex = /^[^"(),:;<>[\]&!%$+=?@À-ÖØ-öø-ÿ]*$/;

export const specialCharactersGlobalRegex = /["(),:;<>[\]&!%$+=?@]/g;

export const digitOnlyRegex = /^\d+$/;

export const digitOrEmptyRegex = /^\d*$/;

export const digitOrAlphaRegex = /^[a-zA-Z0-9]$/;

export const nonDigitNorAlphaGlobalRegex = /[^a-zA-Z0-9]/g;

export const nonDigitGlobalRegex = /[^0-9]/g;

export const alphaOnlyRegex = /^[a-zA-Z]*$/;

export const alphaWithSpacesRegex = /[^a-zA-Z\s]/g;

export const alphaWithSpacesAndSpecialCharsRegex = /^[A-Za-z\s'-]*$/;

export const nameOnCardAllowedCharsRegex = /[^a-zA-Z\s'-]/g;

export const alphaNumericRegex = /[^a-zA-Z0-9]/g;

export const emailRegex =
    /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9]+(?:[.-][a-zA-Z0-9]+)+$/;

export const noSpecialEmailCharsBeforeAt = /^[^"(),:;<>[\]&]+@/;

// Name field validation regex - matches API spec pattern:
// First char: alphanumeric, hyphen, apostrophe, period (no space)
// Remaining chars: alphanumeric, hyphen, apostrophe, period, space
export const allowedSpecialCharsRegex = /^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]*$/;

// Inverse for input filtering - removes characters NOT in the allowed set
// Note: This allows space anywhere; schema validation enforces "no space as first char"
export const notAllowedSpecialCharsGlobalRegex = /[^A-Za-z0-9\-'. ]/g;

export const addressRegex = /^[A-Za-z0-9\-'. /#%@]*$/;

// Inverse of addressRegex - filters out characters NOT in the allowed set
export const notAllowedAddressCharsGlobalRegex = /[^A-Za-z0-9\-'. /#%@]/g;

export const zipRegex = /^\d{5}$/;

/** Only allow a valid invitation code which is also referred to as a finder number or an application ID.*/
export const invitationCodeRegex = /^[KMGZ][01][A-Z0-9]{8}$/i;
export const genericInvitationCodeRegex = /^[G][01][A-Z0-9]{8}$/i;
/** Almost the same as invitationCodeRegex except for that it accepts empty string as well. */
export const uidParameterRegex = /^([KMGZ][01][A-Z0-9]{8})?$/i;
export const purlCodeRegex = /^.+$/i;
/** Almost the same as invitationCodeRegex except for that it only accepts non-generic invitation codes. */
export const applicationIDRegex = /^[KMZ][01][A-Z0-9]{8}$/i;
/** Validates payment reference ID for application ID format (K or M prefix) */
export const paymentApplicationIdRegex = /^[KM][01][a-zA-Z0-9]{8}$/;

/** Special AAA member number that indicates an unknown or invalid member */
export const UNKNOWN_AAA_MEMBER_NUMBER = "0C000000000000000";

export const invitationCodeFirstCharRegex = /^[MGZ]$/i;

export const invitationCodeSecondCharRegex = /^[0]$/;

export const VALIDATION_MESSAGES_CARD = {
    SELECT_OPTION: "Please select an option",
    FIELD_REQUIRED: "This field is required",
    EXPIRY_FORMAT: "Use MM/YY Format",
    CARD_EXPIRED: "Card has expired",
    CVV_LENGTH: "CSV Must be 3 or 4 digits",
    CARD_NUMBER_LENGTH: "Enter a 15 or 16-digit card number",
    INVALID_CARD_NUMBER: "Invalid card number",
    ONLY_LETTERS: "Only letters are allowed",
    INVALID_MONTH: "Invalid month",
};

export const expiryRegex = /^\d{2}\/\d{2}$/;

export const normalizeExpiry = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    if (digits.length < 3) {
        return digits;
    }
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

export const isFutureExpiry = (mmYY: string) => {
    if (!expiryRegex.test(mmYY)) {
        return false;
    }
    const [mmStr, yyStr] = mmYY.split("/");
    const mm = parseInt(mmStr, 10);
    if (mm < 1 || mm > 12) {
        return false;
    }
    const now = new Date();
    const curYY = now.getFullYear() % 100;
    const curMM = now.getMonth() + 1;
    const yy = parseInt(yyStr, 10);
    return yy > curYY || (yy === curYY && mm >= curMM);
};

export const validateCCExpiration = (v: string): string | true => {
    if (!expiryRegex.test(v)) {
        return VALIDATION_MESSAGES_CARD.EXPIRY_FORMAT;
    }
    const [mmStr] = v.split("/");
    const mm = parseInt(mmStr, 10);
    if (mm < 1 || mm > 12) {
        return VALIDATION_MESSAGES_CARD.INVALID_MONTH;
    }
    return isFutureExpiry(v) ? true : VALIDATION_MESSAGES_CARD.CARD_EXPIRED;
};

export const cvvRegex = /^\d{3,4}$/;

export const validateCSV = (v: string) => {
    if (!cvvRegex.test(v)) {
        return VALIDATION_MESSAGES_CARD.CVV_LENGTH || "CSV Must be 3 or 4 digits";
    }
    return true;
};

export const cardNumber16DigitsRegex = /^\d{16}$/;
export const cardNumber15Or16DigitsRegex = /^\d{15,16}$/;

export const luhnCheck = (digitsOnly: string) => {
    const d = digitsOnly.replace(/\D/g, "");
    if (!cardNumber15Or16DigitsRegex.test(d)) {
        return false;
    }
    let sum = 0,
        dbl = false;
    for (let i = d.length - 1; i >= 0; i--) {
        let n = d.charCodeAt(i) - 48;
        if (dbl) {
            n *= 2;
            if (n > 9) n -= 9;
        }
        sum += n;
        dbl = !dbl;
    }
    return sum % 10 === 0;
};

export const validateCardNumber = (val: string | null | undefined): string | true => {
    const digits = String(val ?? "").replace(/\D/g, "");
    if (!cardNumber15Or16DigitsRegex.test(digits)) {
        return VALIDATION_MESSAGES_CARD.CARD_NUMBER_LENGTH || "Enter a 15 or 16-digit card number";
    }
    return luhnCheck(digits) ? true : VALIDATION_MESSAGES_CARD.INVALID_CARD_NUMBER || "Invalid card number";
};

export const validateNameOnCard = (v: string | null | undefined): string | true => {
    const trimmed = String(v ?? "").trim();
    if (!trimmed) return VALIDATION_MESSAGES_CARD.FIELD_REQUIRED;

    alphaWithSpacesRegex.lastIndex = 0;

    return !alphaWithSpacesRegex.test(trimmed) ? true : VALIDATION_MESSAGES_CARD.ONLY_LETTERS;
};

/**
 * Type conversion helper to ensure state value is typed as UsStates
 * @param state - The state abbreviation (may be undefined)
 * @returns The state value typed as UsStates or undefined
 */
export const toUsStates = (state: string | undefined): import("../state/RootSchema").UsStates | undefined =>
    state as import("../state/RootSchema").UsStates | undefined;
